/* Portable cart snapshots. No storage service or customer data is required. */
(function (root, factory) {
  const core = factory();
  if (typeof module === 'object' && module.exports) module.exports = core;
  else root.BnxCartShareCore = core;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const MAX_BYTES = 131072, MAX_TOKEN = 24000, MAX_LINES = 100;
  const fail = message => { throw new Error(message); };
  const integer = (n, max = Number.MAX_SAFE_INTEGER) => Number.isSafeInteger(n) && n > 0 && n <= max;
  const object = o => o !== null && typeof o === 'object' && !Array.isArray(o);
  function safeOptionText(text) {
    // Some themes print properties as HTML. A shared URL must not inject active markup.
    const decoded = text.replace(/&#(x[0-9a-f]+|[0-9]+);?/gi, (all, value) => {
      const n = /^x/i.test(value) ? parseInt(value.slice(1), 16) : Number(value);
      return n > 0 && n < 128 ? String.fromCharCode(n) : all;
    }).replace(/&(colon|Tab|NewLine|lt|gt);/gi, (_, name) => ({colon: ':', tab: '\t', newline: '\n', lt: '<', gt: '>'})[name.toLowerCase()]);
    return !/<[a-z!/?]/i.test(decoded) && !/(javascript|vbscript|data):/i.test(decoded.replace(/[\u0000-\u0020]/g, ''));
  }
  function properties(value) {
    if (value == null) return {};
    if (!object(value) || Object.keys(value).length > 128) fail('This cart has too many custom options to share.');
    const output = Object.create(null);
    for (const [key, val] of Object.entries(value)) {
      if (!key || key.length > 256 || typeof val !== 'string' || val.length > 16384) fail('A custom option cannot be shared safely.');
      if (['__proto__', 'constructor', 'prototype'].includes(key) || !safeOptionText(key) || !safeOptionText(val)) fail('This cart contains custom option markup that cannot be shared safely.');
      Object.defineProperty(output, key, {value: val, enumerable: true, writable: true});
    }
    return output;
  }
  function line(item) {
    if (!object(item)) fail('Invalid saved cart item.');
    const id = item.variant_id ?? item.id;
    const selling_plan = item.selling_plan_allocation?.selling_plan?.id ?? item.selling_plan ?? 0;
    if (!integer(id) || !integer(item.quantity, 9999) || (selling_plan !== 0 && !integer(selling_plan))) fail('Invalid product or quantity in this cart.');
    // Native nested/remote bundles need their own parent relationships, not a flat copy.
    if (item.parent_relationship || item.parent_id || item.parent_line_key || item.item_components?.length || item.components?.length || item.remote) fail('This cart contains a bundle that cannot be shared with this version.');
    return {id, quantity: item.quantity, properties: properties(item.properties), selling_plan,
      title: String(item.product_title || item.title || 'Saved product').slice(0, 240)};
  }
  function lines(items, allowEmpty = false) {
    if (!Array.isArray(items) || (!allowEmpty && !items.length) || items.length > MAX_LINES) fail('Share between 1 and 100 cart items.');
    return items.map(line);
  }
  function snapshot(cart, shop) {
    const data = {v: 1, shop, items: lines(cart.items)};
    validate(data, shop);
    return data;
  }
  function validate(data, shop) {
    if (!object(data) || data.v !== 1 || typeof shop !== 'string' || !shop || data.shop !== shop) fail('This link is not a valid saved cart for this store.');
    return {v: 1, shop, items: lines(data.items)};
  }
  const bytes = value => new TextEncoder().encode(value);
  function toBase64(data) {
    let s = '';
    for (let i = 0; i < data.length; i += 8192) s += String.fromCharCode(...data.subarray(i, i + 8192));
    return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  function fromBase64(value) {
    if (!/^[A-Za-z0-9_-]+$/.test(value) || value.length % 4 === 1) fail('The saved cart link is incomplete. Please copy the entire link.');
    try { return Uint8Array.from(atob(value.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0)); }
    catch { fail('The saved cart link is incomplete. Please copy the entire link.'); }
  }
  async function readBounded(stream) {
    const reader = stream.getReader(), chunks = []; let size = 0;
    try {
      while (true) {
        const {value, done} = await reader.read();
        if (done) break;
        size += value.length;
        if (size > MAX_BYTES) { await reader.cancel(); fail('This saved cart is too large.'); }
        chunks.push(value);
      }
    } finally { reader.releaseLock(); }
    const result = new Uint8Array(size); let offset = 0;
    for (const chunk of chunks) { result.set(chunk, offset); offset += chunk.length; }
    return result;
  }
  async function encode(data) {
    const clean = validate(data, data.shop);
    const packed = [1, clean.shop, clean.items.map(i => [i.id, i.quantity, i.properties, i.selling_plan, i.title])];
    const raw = bytes(JSON.stringify(packed));
    if (raw.length > MAX_BYTES) fail('This cart has too many custom options. Share fewer items at a time.');
    let token = '1r.' + toBase64(raw);
    if (typeof CompressionStream === 'function') {
      try {
        const compressed = await readBounded(new Blob([raw]).stream().pipeThrough(new CompressionStream('gzip')));
        const candidate = '1g.' + toBase64(compressed);
        if (candidate.length < token.length) token = candidate;
      } catch { /* Raw links work in browsers without compression. */ }
    }
    if (token.length > MAX_TOKEN) fail('The share link is too long. Please share fewer items at a time.');
    return token;
  }
  async function decode(token, shop) {
    if (typeof token !== 'string' || token.length > MAX_TOKEN || !/^1[rg]\./.test(token)) fail('This saved cart link is invalid or uses an unsupported version.');
    let raw = fromBase64(token.slice(3));
    if (token.startsWith('1g.')) {
      if (typeof DecompressionStream !== 'function') fail('Please open this link in an up-to-date browser.');
      try { raw = await readBounded(new Blob([raw]).stream().pipeThrough(new DecompressionStream('gzip'))); }
      catch { fail('This saved cart link is damaged or too large. Please request a new link.'); }
    }
    if (raw.length > MAX_BYTES) fail('This saved cart is too large.');
    let value;
    try { value = JSON.parse(new TextDecoder('utf-8', {fatal: true}).decode(raw)); }
    catch { fail('The saved cart link is incomplete. Please copy the entire link.'); }
    if (!Array.isArray(value) || value.length !== 3 || value[0] !== 1 || !Array.isArray(value[2])) fail('Invalid saved cart format.');
    const items = value[2].map(v => {
      if (!Array.isArray(v) || v.length !== 5 || typeof v[4] !== 'string') fail('Invalid saved cart item.');
      return {id: v[0], quantity: v[1], properties: v[2], selling_plan: v[3], title: v[4]};
    });
    return validate({v: value[0], shop: value[1], items}, shop);
  }
  function signature(item) {
    return JSON.stringify([item.id, item.selling_plan || 0, Object.keys(item.properties || {}).sort().map(k => [k, item.properties[k]])]);
  }
  function aggregate(items) {
    const result = new Map();
    for (const item of items) {
      const key = signature(item), existing = result.get(key);
      if (existing) { existing.quantity += item.quantity; if (existing.quantity > 9999) fail('The combined quantity is too high.'); }
      else result.set(key, {...item});
    }
    return result;
  }
  function fingerprint(items) {
    return JSON.stringify([...aggregate(items)].map(([key, i]) => [key, i.quantity]).sort((a, b) => a[0].localeCompare(b[0])));
  }
  function same(a, b) { return fingerprint(a) === fingerprint(b); }
  function plan(current, saved, mode) {
    if (!['merge', 'replace'].includes(mode)) fail('Choose merge or replace.');
    const before = lines(current, true), incoming = lines(saved);
    const target = aggregate(mode === 'merge' ? [...before, ...incoming] : incoming);
    const stage = aggregate(before);
    const additions = [];
    for (const [key, item] of target) {
      const qty = stage.get(key)?.quantity || 0;
      if (item.quantity > qty) { additions.push({...item, quantity: item.quantity - qty}); stage.set(key, {...item}); }
    }
    if (stage.size > MAX_LINES) fail('The combined cart has too many items.');
    return {before, target: [...target.values()], stage: [...stage.values()], additions};
  }
  function requestItem(item) {
    const result = {id: item.id, quantity: item.quantity, properties: item.properties};
    if (item.selling_plan) result.selling_plan = item.selling_plan;
    return result;
  }
  async function restore(saved, mode, api, record, expected) {
    let mutated = false;
    try {
      let cart = await api.get();
      const current = lines(cart.items, true);
      if (expected && !same(current, expected)) fail('Your cart changed. Close this window and open the shared link again to review it.');
      const p = plan(current, saved, mode);
      // Persist before mutations: uncertain writes must never be blindly repeated.
      record({state: 'pending', mode});
      if (p.additions.length) {
        mutated = true;
        await api.add(p.additions.map(requestItem));
      }
      cart = await api.get();
      if (!same(lines(cart.items, true), p.stage)) fail('Some items could not be restored exactly. Review your cart before trying again.');
      if (!same(p.stage, p.target)) {
        // Re-read keys because discounts can change keys after adding items.
        cart = await api.get();
        if (!same(lines(cart.items, true), p.stage)) fail('Your cart changed during restoration. Please review it.');
        const remaining = new Map([...aggregate(p.target)].map(([key, item]) => [key, item.quantity]));
        const updates = Object.create(null);
        for (const raw of cart.items) {
          const item = line(raw), key = signature(item);
          const keep = Math.min(remaining.get(key) || 0, item.quantity);
          remaining.set(key, (remaining.get(key) || 0) - keep);
          if (keep !== item.quantity) {
            if (typeof raw.key !== 'string' || !raw.key) fail('The cart changed. Please reload it before continuing.');
            updates[raw.key] = keep;
          }
        }
        mutated = true;
        await api.update(updates);
      }
      cart = await api.get();
      if (!same(lines(cart.items, true), p.target)) fail('The restored cart differs from the saved selection. Please review it.');
      record({state: 'done', mode});
      return cart;
    } catch (error) {
      if (mutated) {
        try { record({state: 'uncertain', mode}); } catch { /* Existing pending record already prevents retry. */ }
        error.cartMayHaveChanged = true;
      } else { try { record(null); } catch { /* No request was written. */ } }
      throw error;
    }
  }
  return {snapshot, encode, decode, lines, signature, fingerprint, same, plan, restore, MAX_TOKEN};
});
